import { MessagesFilter } from "@/models/messages-filter";
import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import { Transaction } from "@/models/message";

export interface SaveFilters {
  messageFilterId: string;
  messageFilterName: string;
  messageFilterDescription: string;
  messageFilters: MessagesFilter;
}

export interface MessagesModule {
  selectedTransaction: Transaction;
  initialMessageRecords: [];
  filters: MessagesFilter;
  selectedIOType: string;
  isSaveFilterApplied: boolean;
  savedFilter: SaveFilters;
  notifications: [];
  selectedMembers: [];
  details: any;
  refreshIntervalTime: number;
  showSpinner: boolean;
  exportSpinners: boolean[];
}

export const useMessagesStore = defineStore("messages", () => {
  // STATES
  const selectedTransaction = ref<MessagesModule["selectedTransaction"]>(
    new Transaction(),
  );
  const initialMessageRecords = ref(
    <MessagesModule["initialMessageRecords"]>[],
  );
  const filters = ref<MessagesFilter>(new MessagesFilter());
  const selectedIOType = ref<string>("ALL");
  const isSaveFilterApplied = ref<boolean>(false);
  const savedFilter = ref<MessagesModule["savedFilter"]>();
  const notifications = ref<MessagesModule["notifications"]>([]);
  const selectedMembers = ref<MessagesModule["selectedMembers"]>([]);
  const details = ref<MessagesModule["details"]>();
  const refreshIntervalTime = ref<number>(0);
  const showSpinner = ref<boolean>(false);
  const exportSpinners = ref<boolean[]>([]);

  // ACTIONS
  const setSelectedTransaction = (
    transaction: MessagesModule["selectedTransaction"],
  ) => {
    selectedTransaction.value = transaction;
  };
  const setInitialMessageRecords = (payload: []) => {
    initialMessageRecords.value = payload;
  };

  const setFilters = (appliedFilters: MessagesFilter) => {
    filters.value = appliedFilters;
  };
  const setSelectedIOType = (direction: string) => {
    selectedIOType.value = direction;
  };
  const setIsSaveFilterApplied = (filterApplied: boolean) => {
    isSaveFilterApplied.value = filterApplied;
  };
  const setSavedFilter = (savedFilterInfo: MessagesModule["savedFilter"]) => {
    savedFilter.value = savedFilterInfo;
  };

  const setNotifications = (
    notifcationsList: MessagesModule["notifications"],
  ) => {
    notifications.value = notifcationsList;
  };

  const setSelectedMembers = (members: MessagesModule["selectedMembers"]) => {
    selectedMembers.value = members;
  };

  const setDetails = (messageDetails: MessagesModule["details"]) => {
    details.value = messageDetails;
  };

  const setRefreshIntervalTime = (time: number) => {
    refreshIntervalTime.value = time;
  };

  const setShowSpinner = (flag: boolean) => {
    showSpinner.value = flag;
  };

  const setExportSpinners = (spinners: boolean[]) => {
    exportSpinners.value = spinners;
  };

  const getSelectedTransaction: ComputedRef<
    MessagesModule["selectedTransaction"]
  > = computed(() => selectedTransaction.value);

  const getInitialRecords: ComputedRef<
    MessagesModule["initialMessageRecords"]
  > = computed(() => initialMessageRecords.value);

  const getFilters: ComputedRef<MessagesModule["filters"]> = computed(
    () => filters.value,
  );

  const getSelectedIOType: ComputedRef<{}> = computed(
    () => selectedIOType.value,
  );
  const getSavedFilter: ComputedRef<SaveFilters> = computed(
    () =>
      savedFilter.value ?? {
        messageFilterId: "",
        messageFilterName: "",
        messageFilterDescription: "",
        messageFilters: new MessagesFilter(),
      },
  );

  const getNotifications: ComputedRef<MessagesModule["notifications"]> =
    computed(() => notifications.value);

  const getSelectedMembers: ComputedRef<MessagesModule["selectedMembers"]> =
    computed(() => selectedMembers.value);

  const getDetails: ComputedRef<MessagesModule["details"]> = computed(
    () => details.value ?? {},
  );

  const getRefreshIntervalTime: ComputedRef<number> = computed(
    () => refreshIntervalTime.value,
  );

  const getShowSpinner: ComputedRef<boolean> = computed(
    () => showSpinner.value,
  );

  const getExportSpinners: ComputedRef<boolean[]> = computed(
    () => exportSpinners.value,
  );

  return {
    setSelectedTransaction,
    setInitialMessageRecords,
    setFilters,
    setSelectedIOType,
    setIsSaveFilterApplied,
    setSavedFilter,
    setNotifications,
    setSelectedMembers,
    setDetails,
    setRefreshIntervalTime,
    setShowSpinner,
    setExportSpinners,
    getExportSpinners,
    getShowSpinner,
    getRefreshIntervalTime,
    getSelectedMembers,
    getNotifications,
    getDetails,
    getSavedFilter,
    getSelectedIOType,
    getInitialRecords,
    getSelectedTransaction,
    getFilters,
  };
});
